import React from "react";
import { observer } from "mobx-react";
import { Box, Paper, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { RedemptionJourneyStageId } from "../../../../../../models/Dashboard/Redemption/RedemptionSummary";
import { RedemptionDashboardStore } from "../../../../stores/dashboards/RedemptionDashboardStore";
import { formatRedemptionCount } from "./redemptionFormat";
import { RedemptionSectionLabel } from "./RedemptionSection";
import RedemptionHint from "./RedemptionHint";

interface RedemptionJourneyProps {
  store: RedemptionDashboardStore;
}

const stageTone = (
  stageId: RedemptionJourneyStageId,
  colors: {
    purple: string;
    darkBlue: string;
    successGreen: string;
    errorRed: string;
    teal: string;
  },
  warn: string,
): string => {
  if (stageId === "sent") {
    return colors.darkBlue;
  }
  if (stageId === "redeemed") {
    return colors.successGreen;
  }
  if (stageId === "shipments") {
    return colors.teal;
  }
  if (stageId === "pending") {
    return warn;
  }
  if (stageId === "expired") {
    return colors.errorRed;
  }
  return colors.purple;
};

const RedemptionJourney: React.FC<RedemptionJourneyProps> = ({ store }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const stages = store.summaryResult?.data.journey?.stages;

  if (!store.summaryResult || store.summaryError) {
    return null;
  }

  const openStage = (metricId: string) => {
    try {
      void store.openJourney(metricId);
    } catch (error) {
      console.error(error);
    }
  };

  if (!stages || stages.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <RedemptionSectionLabel label="Coupon journey" />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(6, minmax(0, 1fr))",
          },
          gap: 1.75,
        }}
      >
        {stages.map((stage) => {
          const tone = stageTone(
            stage.stageId,
            colors,
            theme.palette.goldenYellow.main,
          );
          return (
            <Paper
              key={stage.metricId}
              variant="outlined"
              role="button"
              tabIndex={0}
              onClick={() => openStage(stage.metricId)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openStage(stage.metricId);
                }
              }}
              sx={{
                p: 2,
                borderRadius: 2,
                cursor: "pointer",
                borderColor: colors.softCardBorder,
                bgcolor: colors.softCardBackground,
                "&:hover": {
                  borderColor: colors.purple,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    bgcolor: tone,
                  }}
                />
                <RedemptionHint how={stage.how} fontSize={14} />
              </Box>
              <CustomTypography
                variant="heading2"
                fontWeight={700}
                color={colors.navyText}
                style={{ letterSpacing: "-0.02em", lineHeight: 1 }}
              >
                {formatRedemptionCount(stage.count)}
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={colors.mutedSlateText}
                sx={{ mt: 0.75 }}
              >
                {stage.label}
              </CustomTypography>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default observer(RedemptionJourney);
